import { EncryptDecryptComponent } from './common/components/encrypt-decrypt/encrypt-decrypt.component'
import { UserManagementComponent } from './features/identity/user-management/user-management.component';
import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { LoginComponent } from './features/identity/login/login.component'
import { deviceResolver } from './common/resolvers/device.resolver'
import { welcomeResolver } from './common/resolvers/welcome.resolver'
import { ForgotPasswordComponent } from './features/identity/forgot-password/forgot-password.component'
import { TemporaryPasscodeComponent } from './features/identity/forgot-password/temporary-passcode/temporary-passcode.component'
import { CreateNewPasswordComponent } from './features/identity/forgot-password/create-new-password/create-new-password.component'
import { MfaCollectionComponent } from './features/identity/mfa/mfa-collection/mfa-collection.component'
import { MfaSelectionComponent } from './features/identity/mfa/mfa-selection/mfa-selection.component'
import { MfaVerificationComponent } from './features/identity/mfa/mfa-verification/mfa-verification.component'
import { TotpSetupComponent } from './features/identity/mfa/totp-setup/totp-setup.component'
import { TotpVerifyComponent } from './features/identity/mfa/totp-verify/totp-verify.component'
import { SecurityQaComponent } from './features/identity/security-qa/security-qa.component'
import { WelcomeComponent } from './features/identity/welcome/welcome.component'
import { MyProfileComponent } from './features/identity/my-profile/my-profile.component'
import { UserCheckinComponent } from './features/identity/user-checkin/user-checkin.component'
import { RoleAuthGuard, RoleAuthGuardActivateChild } from './common/guards/roles.guard'
import { BroadbandFallbackComponent } from './features/orders/broadband-fallback/broadband-fallback.component'
import { OrderLayoutComponent } from './features/orders/order-layout/order-layout.component'
import { OrderStateGuard } from './common/guards/order-state.guard'
import { SamlAuthComponent } from './features/identity/saml-auth/saml-auth.component'
import { SsoCallbackComponent } from './features/identity/sso-callback/sso-callback.component'
import { CustomerSearchComponent } from './features/customer-search/customer-search.component';
import { confirmOrLeaveGuard } from './common/guards/confirm-or-leave.guard';
import { LogoutComponent } from './features/identity/logout/logout.component';
import { LicAuthComponent } from './features/identity/lic-auth/lic-auth.component';
import { TpvComponent } from './features/tpv/tpv.component';

const resetForgotPasswordChildren = [{
  path: '', component: ForgotPasswordComponent
}, {
  path: 'passcode', component: TemporaryPasscodeComponent
}, {
  path: 'create-new', component: CreateNewPasswordComponent,
  data: {
    skipReuse: true
  }
}]

const userChildren = [
  {
    path: 'profileSetting', component: MyProfileComponent
  },
  {
    path: 'timeentry', component: UserCheckinComponent
  },
  {
    path: 'user-management', component: UserManagementComponent
  }
]

const addGlobalResolver = (routes: Routes) => {
  return routes.map(route => {
    if (route.path === '**' || route.path === 'auth/sso/callback') return route;
    if (route.resolve === undefined) {
      route.resolve = {}
    }
    route.runGuardsAndResolvers = 'always'
    route.resolve['device'] = deviceResolver
    return route
  })
}

const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'logout', component: LogoutComponent },
  { path: 'auth/saml', component: SamlAuthComponent },
  { path: 'auth/lic', component: LicAuthComponent },
  { path: 'auth/sso/callback', component: SsoCallbackComponent },
  { path: 'welcome', component: WelcomeComponent, resolve: { welcome: welcomeResolver } },
  { path: '', component: LoginComponent },
  {
    path: 'forgot-password',
    children: resetForgotPasswordChildren,
    data: { label: 'Forgot Password' }
  },
  {
    path: 'reset-password',
    children: resetForgotPasswordChildren,
    data: { label: 'Reset Password', forceReset: true }
  },
  {
    path: 'mfa',
    children: [{
      path: 'collection',
      children: [
        {
          path: '', pathMatch: 'full', redirectTo: ':deviceType'
        },
        {
          path: ':deviceType', component: MfaCollectionComponent,
          data: {
            skipReuse: true
          }
        }]
    },
    {
      path: 'verification',
      children: [{ path: '', pathMatch: 'full', redirectTo: ':deviceType' }, {
        path: ':deviceType', component: MfaVerificationComponent,
      }]
    },
    {
      path: 'selection', component: MfaSelectionComponent
    },
    {
      path: 'totp',
      children: [
        { path: 'setup', component: TotpSetupComponent, data: { skipReuse: true } },
        { path: 'verify', component: TotpVerifyComponent, data: { skipReuse: true } }
      ]
    }]
  },
  { path: 'security-qa', component: SecurityQaComponent, data: { skipReuse: true } },
  { path: 'edc', component: EncryptDecryptComponent },
  { path: 'order', component: OrderLayoutComponent, loadChildren: () => import('./features/orders/orders.module').then(m => m.OrdersModule), canActivate: [RoleAuthGuard, OrderStateGuard], canActivateChild: [RoleAuthGuardActivateChild], canDeactivate: [confirmOrLeaveGuard] },
  { path: 'order', component: OrderLayoutComponent, loadChildren: () => import('./features/orders/orders.module').then(m => m.OrdersModule), canActivate: [RoleAuthGuard, OrderStateGuard], canActivateChild: [RoleAuthGuardActivateChild], canDeactivate: [confirmOrLeaveGuard] },
  { path: 'analytics', loadChildren: () => import('./features/analytics/analytics.module').then(m => m.AnalyticsModule), canActivate: [RoleAuthGuard], canActivateChild: [RoleAuthGuardActivateChild] },
  {
    path: 'user',
    children: userChildren,
    data: { label: 'User' }, canActivate: [RoleAuthGuard], canActivateChild: [RoleAuthGuardActivateChild]
  },
  { path: 'broadband-fallback', component: BroadbandFallbackComponent },
  { path: 'customer-search', component: CustomerSearchComponent },
  { path: 'tpv', component: TpvComponent, canActivate: [RoleAuthGuard], data: { userModules: ['TPV'] } },
  { path: 'bestbuy-barcode', loadComponent: () => import('./features/bestbuy-barcode/bestbuy-barcode.component').then(m => m.BestBuyBarcodeComponent), canActivate: [RoleAuthGuard], data: { userModules: ['BestBuyBarcode'] } },
  { path: 'feature-toggles', loadComponent: () => import('./features/feature-toggles/feature-toggles.component').then(m => m.FeatureTogglesComponent), canActivate: [RoleAuthGuard], data: { userModules: ['FeatureToggleAdmin'] } },
  { path: '**', redirectTo: '' }
]

@NgModule({
  imports: [RouterModule.forRoot(addGlobalResolver(routes), { enableTracing: false })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
